﻿import React, { useEffect, useMemo, useState } from "react";
import {
    Callout,
    DirectionalHint,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    TextField,
    Toggle,
    PrimaryButton,
    DefaultButton,
    IconButton,
    TooltipHost,
    Spinner,
    SpinnerSize,
    Stack,
    MessageBar,
    MessageBarType
} from "@fluentui/react";
import styles from "./FieldSelectionForInvoices.module.css";
import { toast } from "react-toastify";

import { gloggedinuser, gscopedinvrule } from "../../interfaces";
import { qGetScopedInvRules, qUpsertScopedInvRule, qDelScopedInvRule } from "../../api/qpi";
import { tooltipStyles } from "../../styles_glob";

interface ScopedInvRulesCalloutProps {
    target: React.RefObject<HTMLElement> | string;
    loggedInUser: gloggedinuser;
    groupid: string;
    onDismiss: () => void;
}

const emptyRule = (): gscopedinvrule => ({
    supplier_id: "",
    info: "",
    post_validation_rules: { items_not_vat: false, trim_all_types: false },
    pre_main_rules: { override_floor: false, lower_min_to_apply_min_items: false, allow_items_without_qty_and_price: false }
});

const ScopedInvRulesCallout: React.FC<ScopedInvRulesCalloutProps> = ({ target, loggedInUser, groupid, onDismiss }) => {
    const [rules, setRules]               = useState<gscopedinvrule[]>([]);
    const [loading, setLoading]           = useState<boolean>(true);
    const [savingId, setSavingId]         = useState<string | null>(null);
    const [origIdMap, setOrigIdMap]       = useState<Record<string, string>>({});
    const [newRule, setNewRule]           = useState<gscopedinvrule>(emptyRule());
    const [addOpen, setAddOpen]           = useState<boolean>(false);
    const addBtnRef                       = React.useRef<HTMLDivElement>(null);

    const load = async () => {
        if (!groupid) {
            setRules([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await qGetScopedInvRules(loggedInUser, groupid);
            import.meta.env.DEV === true && console.log("‹get_scoped_callout›", data);
            const normalized = (data || []).map(r => ({
                ...r,
                info: r.info ?? "",
                post_validation_rules: r.post_validation_rules || {},
                pre_main_rules: r.pre_main_rules || {}
            }));
            setRules(normalized);
            const m: Record<string, string> = {};
            normalized.forEach(r => { m[r.supplier_id] = r.supplier_id; });
            setOrigIdMap(m);
        } catch (e) {
            toast.error("Failed to load scoped rules", { position: "top-center", autoClose: 2500 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updateRow = (idx: number, mutator: (r: gscopedinvrule) => gscopedinvrule) => {
        setRules(prev => prev.map((r, i) => i === idx ? mutator(r) : r));
    };

    const handleSaveRow = async (idx: number) => {
        const row = rules[idx];
        if (!row.supplier_id.trim()) {
            toast.warn("Supplier ID is required", { position: "top-center", autoClose: 2000 });
            return;
        }
        const orig = origIdMap[row.supplier_id] ?? row.supplier_id;
        setSavingId(row.supplier_id);
        const res: any = await qUpsertScopedInvRule(loggedInUser, row, groupid, orig);
        setSavingId(null);
        if (res && res.status === "success") {
            toast.success("Saved", { position: "top-center", autoClose: 1500 });
            setOrigIdMap(prev => ({ ...prev, [row.supplier_id]: row.supplier_id }));
            load();
        } else {
            toast.error("Save failed", { position: "top-center", autoClose: 2500 });
        }
    };

    const handleDeleteRow = async (idx: number) => {
        const row = rules[idx];
        if (!row.supplier_id) return;
        if (!window.confirm(`Delete rule for supplier "${row.supplier_id}"?`)) return;
        setSavingId(row.supplier_id);
        const res: any = await qDelScopedInvRule(loggedInUser, origIdMap[row.supplier_id] ?? row.supplier_id, groupid);
        setSavingId(null);
        if (res && res.status === "success") {
            toast.success("Deleted", { position: "top-center", autoClose: 1500 });
            load();
        } else {
            toast.error("Delete failed", { position: "top-center", autoClose: 2500 });
        }
    };

    const handleAddNew = async () => {
        if (!newRule.supplier_id.trim()) {
            toast.warn("Supplier ID is required", { position: "top-center", autoClose: 2000 });
            return;
        }
        setSavingId("__new__");
        const res: any = await qUpsertScopedInvRule(loggedInUser, newRule, groupid, newRule.supplier_id);
        setSavingId(null);
        if (res && res.status === "success") {
            toast.success("Added", { position: "top-center", autoClose: 1500 });
            setNewRule(emptyRule());
            setAddOpen(false);
            load();
        } else {
            toast.error("Add failed", { position: "top-center", autoClose: 2500 });
        }
    };

    const columns: IColumn[] = useMemo(() => [
        {
            key: "supplier_id", name: "Supplier ID", fieldName: "supplier_id", minWidth: 140, maxWidth: 200, isResizable: true,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <TooltipHost content="Unique supplier identifier (primary key)" calloutProps={tooltipStyles} directionalHint={DirectionalHint.topAutoEdge}>
                    <TextField
                        value={item.supplier_id}
                        onChange={(_, v) => updateRow(idx!, r => ({ ...r, supplier_id: v || "" }))}
                        styles={{ root: { width: "100%" } }}
                    />
                </TooltipHost>
            )
        },
        {
            key: "info", name: "Info", fieldName: "info", minWidth: 160, maxWidth: 320, isResizable: true,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <TooltipHost content="Free-text note about this supplier rule" calloutProps={tooltipStyles} directionalHint={DirectionalHint.topAutoEdge}>
                    <TextField
                        value={item.info ?? ""}
                        onChange={(_, v) => updateRow(idx!, r => ({ ...r, info: v || "" }))}
                        styles={{ root: { width: "100%" } }}
                    />
                </TooltipHost>
            )
        },
        {
            key: "items_not_vat", name: "No Vat in Items", minWidth: 50, maxWidth: 120,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <TooltipHost content="Post Processing Rule - don't consider possibility that line items are presented with VAT" calloutProps={tooltipStyles} directionalHint={DirectionalHint.topAutoEdge}>
                    <Toggle
                        checked={!!item.post_validation_rules?.items_not_vat}
                        onChange={(_, checked) => updateRow(idx!, r => ({
                            ...r,
                            post_validation_rules: { ...r.post_validation_rules, items_not_vat: !!checked }
                        }))}
                    />
                </TooltipHost>
            )
        },
        {
            key: "trim_all_types", name: "Trim All Types", minWidth: 50, maxWidth: 120,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <TooltipHost content="Post Processing Rule - apply trim among all type values during post validation." calloutProps={tooltipStyles} directionalHint={DirectionalHint.topAutoEdge}>
                    <Toggle
                        checked={!!item.post_validation_rules?.trim_all_types}
                        onChange={(_, checked) => updateRow(idx!, r => ({
                            ...r,
                            post_validation_rules: { ...r.post_validation_rules, trim_all_types: !!checked }
                        }))}
                    />
                </TooltipHost>
            )
        },
        {
            key: "override_floor", name: "Alt Min Items Logic", minWidth: 50, maxWidth: 130,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <TooltipHost content="Pre-Main Processing Rule Alteration - alternative line items floor determination" calloutProps={tooltipStyles} directionalHint={DirectionalHint.topAutoEdge}>
                    <Toggle
                        checked={!!item.pre_main_rules?.override_floor}
                        onChange={(_, checked) => updateRow(idx!, r => ({
                            ...r,
                            pre_main_rules: { ...r.pre_main_rules, override_floor: !!checked }
                        }))}
                    />
                </TooltipHost>
            )
        },
        {
            key: "lower_min_to_apply_min_items", name: "Lower Item Floor Trigger", minWidth: 50, maxWidth: 200,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <TooltipHost content="Pre-Main Processing Rule Alteration - lower requirements for activation of line items floor" calloutProps={tooltipStyles} directionalHint={DirectionalHint.topAutoEdge}>
                    <Toggle
                        checked={!!item.pre_main_rules?.lower_min_to_apply_min_items}
                        onChange={(_, checked) => updateRow(idx!, r => ({
                            ...r,
                            pre_main_rules: { ...r.pre_main_rules, lower_min_to_apply_min_items: !!checked }
                        }))}
                    />
                </TooltipHost>
            )
        },
        {
            key: "allow_items_without_qty_and_price", name: "Relax Inclusion Requirements", minWidth: 80, maxWidth: 230,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <TooltipHost content="Handling Rule - relax line item inclusion requirements if no other items are present. Targeting handwritten invoices without quantity and per item price information." calloutProps={tooltipStyles} directionalHint={DirectionalHint.topAutoEdge}>
                    <Toggle
                        checked={!!item.pre_main_rules?.allow_items_without_qty_and_price}
                        onChange={(_, checked) => updateRow(idx!, r => ({
                            ...r,
                            pre_main_rules: { ...r.pre_main_rules, allow_items_without_qty_and_price: !!checked }
                        }))}
                    />
                </TooltipHost>
            )
        },
        {
            key: "actions", name: "", minWidth: 80, maxWidth: 90,
            onRender: (item: gscopedinvrule, idx?: number) => (
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                    <TooltipHost content="Save changes" calloutProps={tooltipStyles}>
                        <IconButton
                            iconProps={{ iconName: "Save" }}
                            onClick={() => handleSaveRow(idx!)}
                            disabled={savingId === item.supplier_id}
                        />
                    </TooltipHost>
                    <TooltipHost content="Delete row" calloutProps={tooltipStyles}>
                        <IconButton
                            iconProps={{ iconName: "Delete" }}
                            onClick={() => handleDeleteRow(idx!)}
                            disabled={savingId === item.supplier_id}
                        />
                    </TooltipHost>
                </Stack>
            )
        }
    ], [savingId, origIdMap, rules]);

    return (
        <Callout
            target={target as any}
            onDismiss={onDismiss}
            directionalHint={DirectionalHint.bottomLeftEdge}
            gapSpace={8}
            isBeakVisible={false}
            setInitialFocus
            preventDismissOnEvent={() => addOpen}
            styles={{ root: { maxWidth: "min(1200px, 96vw)" } }}
        >
            <div style={{ padding: 16, minWidth: 900, maxWidth: "min(1200px, 96vw)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#bf498e" }}>Supplier-Scoped Invoice Rules</div>
                    <DefaultButton text="Refresh" iconProps={{ iconName: "Refresh" }} onClick={load} disabled={loading} />
                </div>

                <MessageBar messageBarType={MessageBarType.info} isMultiline={false} styles={{ root: { marginBottom: 8 } }}>
                    Rules apply per supplier and override defaults during invoice pre-main / post-validation processing.
                </MessageBar>

                {loading ? (
                    <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
                        <Spinner size={SpinnerSize.large} label="Loading..." />
                    </div>
                ) : (
                    <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
                        <DetailsList
                            items={rules}
                            columns={columns}
                            setKey="supplier_id"
                            layoutMode={DetailsListLayoutMode.justified}
                            selectionMode={SelectionMode.none}
                            isHeaderVisible={true}
                            styles={{
                                root: {
                                    selectors: {
                                        ".ms-DetailsHeader-cell": {
                                            height: "auto",
                                            alignItems: "flex-start"

                                        },
                                        ".ms-DetailsHeader-cellTitle": {
                                            whiteSpace: "normal",
                                            overflow: "visible",
                                            textOverflow: "unset",
                                            lineHeight: "14px",
                                            paddingTop: 4,
                                            paddingBottom: 4,
                                            verticalAlign: "top",
                                            alignItems: "center",
                                            height: "45px"
                                        },
                                        ".ms-DetailsHeader-cellName": {
                                            fontSize: "11px",
                                            lineHeight: "14px",
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                            maxHeight: "30px",
                                            overflow: "hidden",
                                            alignItems: "center",
                                        },
                                        '[data-item-key="supplier_id"] .ms-DetailsHeader-cellName': {
                                            fontSize: "15px !important",
                                            maxHeight: "40px !important",
                                        },
                                        '[data-item-key="info"] .ms-DetailsHeader-cellName': {
                                            fontSize: "15px !important",
                                            maxHeight: "40px !important",
                                        }
                                    }
                                }
                            }}
                        />
                        {rules.length === 0 && (
                            <div style={{ padding: 12, color: "#888", fontStyle: "italic" }}>No supplier rules defined.</div>
                        )}
                    </div>
                )}

                <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px dashed #ccc", display: "flex", justifyContent: "flex-end" }}>
                    <div ref={addBtnRef}>
                        <PrimaryButton
                            text="Add New Rule"
                            iconProps={{ iconName: "Add" }}
                            onClick={() => { setNewRule(emptyRule()); setAddOpen(true); }}
                        />
                    </div>
                </div>

                {addOpen && (
                    <Callout
                        target={addBtnRef}
                        onDismiss={() => setAddOpen(false)}
                        directionalHint={DirectionalHint.topRightEdge}
                        gapSpace={8}
                        isBeakVisible={false}
                        setInitialFocus
                        styles={{ root: { maxWidth: "min(560px, 92vw)" } }}
                    >
                        <div style={{ padding: 16, minWidth: 380, maxWidth: "min(560px, 92vw)" }}>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: "#bf498e" }}>Add New Supplier Rule</div>
                            <Stack tokens={{ childrenGap: 10 }}>
                                <TextField
                                    label="Supplier ID"
                                    required
                                    value={newRule.supplier_id}
                                    onChange={(_, v) => setNewRule(r => ({ ...r, supplier_id: v || "" }))}
                                />
                                <TextField
                                    label="Info"
                                    value={newRule.info ?? ""}
                                    onChange={(_, v) => setNewRule(r => ({ ...r, info: v || "" }))}
                                />
                                <TooltipHost content="Post-Processing Rule - treat line items as net (no VAT) during validation." calloutProps={tooltipStyles}>
                                    <Toggle
                                        label="No Vat in Items"
                                        checked={!!newRule.post_validation_rules.items_not_vat}
                                        onChange={(_, c) => setNewRule(r => ({ ...r, post_validation_rules: { ...r.post_validation_rules, items_not_vat: !!c } }))}
                                    />
                                </TooltipHost>
                                <TooltipHost content="Post-Processing Rule - trim/normalize all type values during post validation." calloutProps={tooltipStyles}>
                                    <Toggle
                                        label="Trim All Types"
                                        checked={!!newRule.post_validation_rules.trim_all_types}
                                        onChange={(_, c) => setNewRule(r => ({ ...r, post_validation_rules: { ...r.post_validation_rules, trim_all_types: !!c } }))}
                                    />
                                </TooltipHost>
                                <TooltipHost content="Pre-Processing Rule - override minimum/floor thresholds before main extraction." calloutProps={tooltipStyles}>
                                    <Toggle
                                        label="Override Floor"
                                        checked={!!newRule.pre_main_rules.override_floor}
                                        onChange={(_, c) => setNewRule(r => ({ ...r, pre_main_rules: { ...r.pre_main_rules, override_floor: !!c } }))}
                                    />
                                </TooltipHost>
                                <TooltipHost content="Post-Processing Rule - Lower the minimum threshold for applying min-items logic." calloutProps={tooltipStyles}>
                                    <Toggle
                                        label="Lower Item Min Floor"
                                        checked={!!newRule.pre_main_rules.lower_min_to_apply_min_items}
                                        onChange={(_, c) => setNewRule(r => ({ ...r, pre_main_rules: { ...r.pre_main_rules, lower_min_to_apply_min_items: !!c } }))}
                                    />
                                </TooltipHost>
                                <TooltipHost content="Pre-Processing Rule - relax line item inclusion requirements if no other items are present." calloutProps={tooltipStyles}>
                                    <Toggle
                                        label="Relax Inclusion"
                                        checked={!!newRule.pre_main_rules.allow_items_without_qty_and_price}
                                        onChange={(_, c) => setNewRule(r => ({ ...r, pre_main_rules: { ...r.pre_main_rules, allow_items_without_qty_and_price: !!c } }))}
                                    />
                                </TooltipHost>
                                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" styles={{ root: { marginTop: 6 } }}>
                                    <DefaultButton text="Cancel" onClick={() => setAddOpen(false)} disabled={savingId === "__new__"} />
                                    <PrimaryButton
                                        text="Add"
                                        iconProps={{ iconName: "Add" }}
                                        onClick={handleAddNew}
                                        disabled={savingId === "__new__"}
                                    />
                                </Stack>
                            </Stack>
                        </div>
                    </Callout>
                )}
            </div>
        </Callout>
    );
};

export default ScopedInvRulesCallout;
